package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.InspecaoCompleta
import br.com.htms.inspeccaocampo.platform.PlatformFiles
import br.com.htms.inspeccaocampo.platform.formatDateBr

object SharedExporter {
    suspend fun exportarPdf(inspecao: InspecaoCompleta): String {
        val bytes = when (inspecao.formTipo) {
            FormTipo.INSPECAO_CAMPO -> JpegPdfBuilder.build(inspecao)
            FormTipo.RELATORIO_FOTO_ALOJAMENTO -> JpegPdfBuilder.buildRelatorio(inspecao)
            FormTipo.CHECKLIST_ALOJAMENTO -> JpegPdfBuilder.buildChecklist(inspecao)
            FormTipo.RELATORIO_ALOJAMENTO -> JpegPdfBuilder.buildRelatorioAlojamento(inspecao)
            FormTipo.COMUNICACAO_OCORRENCIAS -> JpegPdfBuilder.buildComunicacao(inspecao)
        }
        val path = PlatformFiles.join(PlatformFiles.cacheDir(), ExportNames.pdf(inspecao))
        PlatformFiles.writeBytes(path, bytes)
        return path
    }

    suspend fun exportarExcel(inspecao: InspecaoCompleta): String {
        val bytes = SharedExcelBuilder.build(inspecao)
        val path = PlatformFiles.join(PlatformFiles.cacheDir(), ExportNames.excel(inspecao))
        PlatformFiles.writeBytes(path, bytes)
        return path
    }

    suspend fun exportarConsolidado(
        items: List<ConsolidadoInspecao>,
        deLabel: String,
        ateLabel: String
    ): String {
        val bytes = SharedExcelConsolidado.build(items)
        val path = PlatformFiles.join(
            PlatformFiles.cacheDir(),
            ExportNames.excelConsolidado(deLabel, ateLabel, items.size)
        )
        PlatformFiles.writeBytes(path, bytes)
        return path
    }

    suspend fun exportarMesZip(lista: List<InspecaoCompleta>, ano: Int, mes: Int): String {
        val entries = linkedMapOf<String, ByteArray>()
        lista.forEach { insp ->
            val base = ExportNames.baseName(insp)
            val pdfBytes = when (insp.formTipo) {
                FormTipo.INSPECAO_CAMPO -> JpegPdfBuilder.build(insp)
                FormTipo.RELATORIO_FOTO_ALOJAMENTO -> JpegPdfBuilder.buildRelatorio(insp)
                FormTipo.CHECKLIST_ALOJAMENTO -> JpegPdfBuilder.buildChecklist(insp)
                FormTipo.RELATORIO_ALOJAMENTO -> JpegPdfBuilder.buildRelatorioAlojamento(insp)
                FormTipo.COMUNICACAO_OCORRENCIAS -> JpegPdfBuilder.buildComunicacao(insp)
            }
            entries["$base.pdf"] = pdfBytes
            entries["$base.xlsx"] = SharedExcelBuilder.build(insp)
        }
        val zip = ZipStoreWriter.write(entries)
        val path = PlatformFiles.join(
            PlatformFiles.cacheDir(),
            ExportNames.zipMes(ano, mes, lista.size)
        )
        PlatformFiles.writeBytes(path, zip)
        return path
    }
}

object ExportNames {
    fun baseName(inspecao: InspecaoCompleta): String =
        baseNameFromParts(
            formTipo = inspecao.formTipo,
            dataHorarioMs = inspecao.dataHorarioMsResumo(),
            responsavel = inspecao.responsavelResumo(),
            obra = inspecao.obraResumo(),
            localTexto = inspecao.localResumo()
        )

    fun baseNameFromParts(
        dataHorarioMs: Long,
        tstResponsavel: String,
        obra: String?,
        localTexto: String
    ): String = baseNameFromParts(
        formTipo = FormTipo.INSPECAO_CAMPO,
        dataHorarioMs = dataHorarioMs,
        responsavel = tstResponsavel,
        obra = obra,
        localTexto = localTexto
    )

    fun baseNameFromParts(
        formTipo: FormTipo,
        dataHorarioMs: Long,
        responsavel: String,
        obra: String?,
        localTexto: String
    ): String {
        val prefix = when (formTipo) {
            FormTipo.INSPECAO_CAMPO -> "Inspecao"
            FormTipo.RELATORIO_FOTO_ALOJAMENTO -> "RelatorioFotoAlojamento"
            FormTipo.CHECKLIST_ALOJAMENTO -> "ChecklistAlojamento"
            FormTipo.RELATORIO_ALOJAMENTO -> "RelatorioAlojamento"
            FormTipo.COMUNICACAO_OCORRENCIAS -> "ComunicacaoOcorrencias"
        }
        val data = formatDateBr(dataHorarioMs).replace("/", "-").take(10)
        val resp = sanitizar(responsavel)
        val obraSan = sanitizar(obra.orEmpty())
            .ifBlank {
                sanitizar(localTexto.lineSequence().firstOrNull().orEmpty())
            }
            .ifBlank { "SemObra" }
        return "${prefix}_${data}_${resp}_$obraSan"
    }

    fun pdf(inspecao: InspecaoCompleta) = "${baseName(inspecao)}.pdf"
    fun excel(inspecao: InspecaoCompleta) = "${baseName(inspecao)}.xlsx"

    /** Nome do PDF remoto: usa o gravado no servidor ou reconstrói pelo summary. */
    fun remotePdf(
        stored: String?,
        dataHorarioMs: Long?,
        createdAt: Long,
        tstResponsavel: String,
        obra: String?,
        localTexto: String
    ): String =
        stored?.takeIf { it.isNotBlank() }
            ?: "${baseNameFromParts(dataHorarioMs ?: createdAt, tstResponsavel, obra, localTexto)}.pdf"

    fun remoteExcel(
        stored: String?,
        dataHorarioMs: Long?,
        createdAt: Long,
        tstResponsavel: String,
        obra: String?,
        localTexto: String
    ): String =
        stored?.takeIf { it.isNotBlank() }
            ?: "${baseNameFromParts(dataHorarioMs ?: createdAt, tstResponsavel, obra, localTexto)}.xlsx"

    fun zipMes(ano: Int, mes: Int, qtd: Int): String {
        val mesStr = (mes + 1).toString().padStart(2, '0')
        return "Inspecoes_${mesStr}-${ano}_${qtd}itens.zip"
    }

    fun excelConsolidado(deLabel: String, ateLabel: String, qtd: Int): String {
        val de = sanitizar(deLabel.ifBlank { "inicio" }, 12)
        val ate = sanitizar(ateLabel.ifBlank { "fim" }, 12)
        return "Consolidado_${de}_a_${ate}_${qtd}itens.xlsx"
    }

    private fun sanitizar(texto: String, maxLen: Int = 28): String =
        texto.trim()
            .replace(Regex("[\\\\/:*?\"<>|]"), "")
            .replace(Regex("\\s+"), "_")
            .filter { it.isLetterOrDigit() || it == '_' || it == '-' }
            .take(maxLen)
            .ifBlank { "SemNome" }
}
